/**
 * Turns the English text a screen shows into another language, from a dictionary keyed
 * by that English text. The screens are written in English; nothing in them knows about
 * languages. A text the dictionary does not have is shown as written.
 *
 * Two kinds of entry:
 *  - a whole text or a piece of one: "Save changes", "Reassign " (a piece keeps the
 *    spaces around it, so pieces placed next to inserted values still read as a sentence);
 *  - a shape, for text built with values inside it: "{} clients", "Due in {} day{}".
 *    Each `{}` stands for one inserted value, filled back in the same order.
 */
export type Dictionary = Readonly<Record<string, string>>;
export type Translate = (text: string) => string;

const PLACEHOLDER = '{}';
const SPACES = /\s+/g;
const HAS_LETTER = /\p{L}/u;
const LETTERS = /\p{L}/gu;
/**
 * A shape with little fixed text ("{} of {}", "Day {}") fits almost any text, so it is
 * only trusted around values that are numbers or texts the dictionary knows: "3 of 12"
 * and "Status: Pending" are turned, "Bank of America" is left alone. With this much
 * fixed text a shape is trusted around anything.
 */
const STRONG_SHAPE_LETTERS = 8;
/** Results of the shape search kept between renders; it is the only costly step. */
const REMEMBERED_TEXTS = 4000;
/** A count in front of a text: "3 clients" is "3" and the known text "clients". */
const COUNTED = /^(\d[\d.,]*) (\S.*)$/s;

interface Shape {
  /** The text before the first value, between the values, and after the last one. */
  first: string;
  inner: readonly string[];
  last: string;
  /** The same for the translation. */
  pieces: readonly string[];
  strong: boolean;
}

function toShape(source: string, target: string): Shape | null {
  const fixed = source.split(PLACEHOLDER);
  const pieces = target.split(PLACEHOLDER);
  const letters = fixed.join('').match(LETTERS)?.length ?? 0;
  // The translation must take the same values, and a shape with no words in it
  // ("{} · {}") would fit any text at all.
  if (pieces.length !== fixed.length || letters < 2) return null;
  const [first, ...inner] = fixed;
  const last = inner.pop() ?? '';
  return { first, inner, last, pieces, strong: letters >= STRONG_SHAPE_LETTERS };
}

/**
 * The values `text` holds if it has this shape, or null. One pass over the text: it must
 * start and end with the outer pieces, and each inner piece is taken at its first place.
 */
function valuesIn(text: string, { first, inner, last }: Shape): string[] | null {
  const end = text.length - last.length;
  if (end < first.length || !text.startsWith(first) || !text.endsWith(last)) return null;
  const values: string[] = [];
  let at = first.length;
  for (const piece of inner) {
    const found = text.indexOf(piece, at);
    if (found === -1 || found + piece.length > end) return null;
    values.push(text.slice(at, found));
    at = found + piece.length;
  }
  values.push(text.slice(at, end));
  return values;
}

export function makeTranslator(dictionary: Dictionary): Translate {
  const whole = new Map<string, string>();
  const shapes: Shape[] = [];
  // Longest first, so the most specific shape that fits is the one used.
  const bySize = Object.entries(dictionary).sort(([a], [b]) => b.length - a.length);
  for (const [source, target] of bySize) {
    if (!source.includes(PLACEHOLDER)) whole.set(source, target);
    else {
      const shape = toShape(source, target);
      if (shape) shapes.push(shape);
    }
  }
  const remembered = new Map<string, string | null>();

  const fromShape = (text: string): string | null => {
    for (const shape of shapes) {
      const values = valuesIn(text, shape);
      if (!values) continue;
      if (!shape.strong && values.some((value) => HAS_LETTER.test(value) && !whole.has(value))) continue;
      return shape.pieces
        .map((piece, index) => (index === 0 ? piece : (whole.get(values[index - 1]) ?? values[index - 1]) + piece))
        .join('');
    }
    return null;
  };

  const counted = (text: string): string | null => {
    const parts = COUNTED.exec(text);
    const known = parts ? whole.get(parts[2]) : undefined;
    return parts && known !== undefined ? `${parts[1]} ${known}` : null;
  };

  // A text built with values: searched for among the shapes once, then remembered.
  const built = (text: string): string | null => {
    // Most of what a screen shows is values — amounts, dates — with no words at all.
    if (!HAS_LETTER.test(text)) return null;
    const known = remembered.get(text);
    if (known !== undefined) return known;
    const found = fromShape(text) ?? counted(text);
    if (remembered.size >= REMEMBERED_TEXTS) remembered.clear();
    remembered.set(text, found);
    return found;
  };

  return (text: string): string => {
    // Nearly every text arrives exactly as the dictionary keys it.
    const direct = whole.get(text);
    if (direct !== undefined) return direct;
    const start = text.length - text.trimStart().length;
    const end = text.trimEnd().length;
    if (end <= start) return text;
    const core = text.slice(start, end).replace(SPACES, ' ');
    const found = whole.get(core) ?? built(core);
    return found == null ? text : text.slice(0, start) + found + text.slice(end);
  };
}

export const asWritten: Translate = (text) => text;
